import React, { useState, useEffect } from 'react';
import { InputLabel, Box, Divider, MenuItem, FormControl, FormControlLabel, Switch, Button, Typography, Tooltip, IconButton, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { CircularProgress } from '@mui/material';
import Select from '@mui/material/Select';
import jwt_decode from "jwt-decode";
import axios from 'axios';
import saveAs from 'file-saver';
import PDFlogo from '../../assets/medias/pdf.png';
import DOClogo from '../../assets/medias/doc.png';
import CSVlogo from '../../assets/medias/csv.png';
import RefreshIcon from '@mui/icons-material/Refresh';

import Sidebar from "../../scenes/global/Sidebar";
import Topbar from "../../scenes/global/Topbar";
import { AssessmentResultStack } from '../../components/AssessmentTabResultsComponents';
import CourseImprovementsPanel from '../../components/CourseImprovementsPanel';
import { buttonStyle } from '../../components/styledComponents'
import { useMessageService } from '../../services/MessageService';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { transformAssessments, formatLatestDate, localizeAssessmentType } from "../../utils/ObjectsUtils";
import { prepareChartData, prepareCommentData } from "../../utils/ChartDataUtils";
import { ASSESSMENT_TYPES_BY_USER, TEACHER_ASSESSMENT_TYPES, UserType, CourseImprovementStatus } from "../../utils/enums";
import { BACKEND_URL } from "../../config";
import { useLanguage } from '../../contexts/LanguageContext';
import { groupQuestionsByWorkshop, getWorkshopDetailsById } from "../../utils/SurveyUtils";
import { getAssessmentTypeConfig } from "../../utils/assessmentTypeConfig";

const ANONYMOUS_PARTICIPANT = '__anonymous__';
// The AI course improvement report can take up to ~15 min: how often to check if it is ready.
const COURSE_IMPROVEMENT_POLL_INTERVAL_MS = 10000;

const CATEGORY_ORDER = ASSESSMENT_TYPES_BY_USER[UserType.TEACHER_TRAINER];

const Reports = () => {

    const [monitorings, setMonitorings] = useState([]);
    const [selectedMonitoring, setSelectedMonitoring] = useState('');
    const [selectedMonitoringId, setSelectedMonitoringId] = useState('');
    const [assessments, setAssessments] = useState([]);
    const [currentUserId, setCurrentUserId] = useState('');
    const { languageCode } = useLanguage();

    const [selectedUser, setSelectedUser] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [teachers, setTeachers] = useState([]);

    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedDayAssessments, setSelectedDayAssessments] = useState([]);
    const [hideRankingNames, setHideRankingNames] = useState(false);

    const [totalAssessments, setTotalAssessments] = useState(0)
    const [latestResponseDate, setLatestResponseDate] = useState('');

    const [chartData, setChartData] = useState([]);
    const [commentData, setCommentData] = useState([]);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isExportingDOCX, setIsExportingDOCX] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showPercentage, setShowPercentage] = useState(false);
    const [isRequestingCourseImprovements, setIsRequestingCourseImprovements] = useState(false);
    const [reportsView, setReportsView] = useState('results');
    const [courseImprovement, setCourseImprovement] = useState(null);
    // State of the latest generation attempt when it is pending, failed or timed out.
    const [courseImprovementJob, setCourseImprovementJob] = useState(null);
    const [isLoadingCourseImprovement, setIsLoadingCourseImprovement] = useState(false);
    const [courseImprovementsError, setCourseImprovementsError] = useState(false);

    //AI summary states
    const [aiSummaries, setAiSummaries] = useState({});
    const [loadingSummaries, setLoadingSummaries] = useState({});

    // for i18n
    const { getMessage } = useMessageService();
    const { currentUser } = useAuthUser();

    // DEBUG
    useEffect(() => {

        console.log("Assessments for this monitoring", assessments);
        console.log("days", days);

    }, [assessments, days]);

    useEffect(() => {
    console.log("Updating selectedDay based on days:", days);
    if (days.length === 0) {
        setSelectedDay('');
        console.log("Resetting selectedDay to empty string");
    } else if (!days.includes(selectedDay)) {
        setSelectedDay(days[0]);
        console.log("Setting selectedDay to first day:", days[0]);
    }
}, [days, selectedDay]);

    /**
     * Fetches the latest completed course improvement of a monitoring and the state of the
     * latest generation attempt (`{ courseImprovement, currentJob }`).
     */
    const fetchCourseImprovementState = async (monitoringId) => {
        const response = await axios.get(
            `${BACKEND_URL}/aiBeacon/monitorings/${monitoringId}/course-improvements`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        return response.data;
    };

    useEffect(() => {
        const hasAiBeaconAccess = Boolean(currentUser?.aiBeaconApiKeyCreatedAt);
        const isSyncedMonitoring = Boolean(selectedMonitoring?.courseAiBeaconId);
        if (!hasAiBeaconAccess || !isSyncedMonitoring || !selectedMonitoring?._id) {
            setCourseImprovement(null);
            setCourseImprovementJob(null);
            setIsLoadingCourseImprovement(false);
            setReportsView('results');
            return;
        }

        let cancelled = false;
        const fetchLatest = async () => {
            setIsLoadingCourseImprovement(true);
            setCourseImprovementsError(false);
            try {
                const data = await fetchCourseImprovementState(selectedMonitoring._id);
                if (!cancelled) {
                    setCourseImprovement(data?.courseImprovement || null);
                    setCourseImprovementJob(data?.currentJob || null);
                }
            } catch (error) {
                console.error('Error fetching course improvements:', error);
                if (!cancelled) {
                    setCourseImprovement(null);
                    setCourseImprovementJob(null);
                }
            } finally {
                if (!cancelled) setIsLoadingCourseImprovement(false);
            }
        };

        fetchLatest();
        return () => {
            cancelled = true;
        };
    }, [currentUser?.aiBeaconApiKeyCreatedAt, selectedMonitoring?._id, selectedMonitoring?.courseAiBeaconId]);

    // While a report is being generated (it runs in the background), check regularly if it is
    // ready. Keeps going if the user switches view or reloads (the pending job is fetched above).
    const isCourseImprovementPending = courseImprovementJob?.status === CourseImprovementStatus.PENDING;
    useEffect(() => {
        if (!isCourseImprovementPending || !selectedMonitoring?._id) return;

        let cancelled = false;
        const intervalId = setInterval(async () => {
            try {
                const data = await fetchCourseImprovementState(selectedMonitoring._id);
                if (cancelled) return;
                setCourseImprovementJob(data?.currentJob || null);
                if (data?.currentJob?.status !== CourseImprovementStatus.PENDING) {
                    setCourseImprovement(data?.courseImprovement || null);
                }
            } catch (error) {
                // Transient error: keep waiting, the next check will retry.
            }
        }, COURSE_IMPROVEMENT_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [isCourseImprovementPending, selectedMonitoring?._id]);


    // fetch the assessments for a the selected monitoring
    useEffect(() => {

        if (selectedMonitoring){
            console.log("fetching for:", selectedMonitoring._id);

            const fetchAssessments = async () => {
    
                const token = localStorage.getItem('token');
                if (!token) {
                    console.log('No token found');
                    return;
                }
        
                try {
                    const response = await axios.get(`${BACKEND_URL}/assessments/monitoring/${selectedMonitoring._id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
        
                    if (response.data && response.data.length > 0) {
                        const transformedAssessments = transformAssessments(response.data);
                        // Spread transformed assessments into the main assessments array
                        setAssessments(transformedAssessments);

                        // extract the days from the assessments
                        const days = transformedAssessments.map(assessment => assessment.day);

                        // convert to a set to remove duplicates, then convert back to array
                        setDays(Array.from(new Set(days)));
                    }

                } catch (error) {
                    console.error('Failed to fetch assessments:', error);
                }
            };

            fetchAssessments();
        }
    }, [selectedMonitoring]);


    useEffect(() => {
        let cancelled = false;

        if (selectedDay && selectedMonitoring){
            filterAssessmentsByDay(() => cancelled);
        }

        return () => {
            cancelled = true;
        };
    }, [selectedDay, selectedMonitoring, assessments]);

    // fetch all the monitorings of the current user when the page loads
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.log('No token found');
            return;
        }

        const decodedToken = jwt_decode(token);
        setCurrentUserId(decodedToken._id);

        const fetchMonitorings = async () => {
            try {
                const response = await axios.get(`${BACKEND_URL}/monitorings`, {
                    headers: {Authorization: `Bearer ${token}`}
                });

                setMonitorings(response.data.monitorings);
            } catch (error) {
                console.error('Failed to fetch monitorings:', error);
            }
        };

        fetchMonitorings();
    }, []);

    // Add this useEffect hook right after the other useEffect hooks in the component
    useEffect(() => {
        const fetchLastResponse = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                console.log('No token found');
                return;
            }

            try {
                const response = await axios.get(`${BACKEND_URL}/responses/last`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data) {
                    const lastResponse = response.data;
                    const monitoringId = lastResponse.monitoringId;
                    const assessmentId = lastResponse.assessmentId;

                    // Find the monitoring from monitorings
                    const monitoring = monitorings.find(m => m._id === monitoringId);
                    if (monitoring) {
                        setSelectedMonitoring(monitoring);
                        setSelectedMonitoringId(monitoringId);

                        // Fetch assessments for this monitoring to get the day
                        const assessmentsResponse = await axios.get(`${BACKEND_URL}/assessments/monitoring/${monitoringId}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });

                        if (assessmentsResponse.data && assessmentsResponse.data.length > 0) {
                            const transformedAssessments = transformAssessments(assessmentsResponse.data);
                            const assessment = transformedAssessments.find(a => a._id === assessmentId);
                            if (assessment) {
                                setSelectedDay(assessment.day);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch last response:', error);
            }
        };

        if (currentUser && currentUser._id && monitorings.length > 0) {
            fetchLastResponse();
        }
    }, [currentUser, monitorings]);

    const isStudentType = (type) => TEACHER_ASSESSMENT_TYPES.includes(type);

    const applyResultFilters = (assessmentsWithResponses, participantId, teacherId) => {
        return assessmentsWithResponses.map((item) => {
            let responses = item.responses || [];

            if (isStudentType(item.type) && teacherId) {
                responses = responses.filter((response) => {
                    const user = response.userId;
                    const userId = user && typeof user === 'object'
                        ? String(user._id || user.id || '')
                        : String(user || '');
                    return userId === String(teacherId);
                });
            }

            if (participantId) {
                if (isStudentType(item.type) && currentUser?.userStatus === UserType.TEACHER_TRAINER) {
                    responses = [];
                } else if (!isStudentType(item.type) || currentUser?.userStatus === UserType.TEACHER) {
                    responses = responses.filter((response) => {
                        const displayName = (response.displayName || '').trim();
                        if (participantId === ANONYMOUS_PARTICIPANT) {
                            return !displayName;
                        }
                        return displayName.toLowerCase() === participantId.toLowerCase();
                    });
                }
            }

            return { ...item, responses };
        });
    };

    const updateChartsFromAssessments = (assessmentsWithResponses, participantId, teacherId) => {
        const filteredAssessments = applyResultFilters(
            assessmentsWithResponses,
            participantId,
            teacherId
        );
        setChartData(handlePrepareChartData(filteredAssessments));
        setCommentData(handlePrepareCommentData(filteredAssessments));

        const allResponses = filteredAssessments.flatMap((assessment) => assessment.responses);
        const latestDate = getLatestResponseDate(allResponses);
        setLatestResponseDate(latestDate ? formatLatestDate(latestDate) : null);
    };

    const buildParticipants = (assessmentsWithResponses) => {
        const participantsByName = new Map();

        assessmentsWithResponses.forEach((assessment) => {
            (assessment.responses || []).forEach((response) => {
                const displayName = (response.displayName || '').trim();
                if (displayName) {
                    const key = displayName.toLowerCase();
                    if (!participantsByName.has(key)) {
                        participantsByName.set(key, { id: displayName, label: displayName });
                    }
                    return;
                }

                if (!isStudentType(assessment.type)) {
                    participantsByName.set(ANONYMOUS_PARTICIPANT, {
                        id: ANONYMOUS_PARTICIPANT,
                        label: getMessage('label_anonymous'),
                    });
                }
            });
        });

        return Array.from(participantsByName.values()).sort((a, b) => a.label.localeCompare(b.label));
    };

    const buildTeachers = (assessmentsWithResponses) => {
        const teachersById = new Map();

        assessmentsWithResponses.forEach((assessment) => {
            if (!isStudentType(assessment.type)) {
                return;
            }
            (assessment.responses || []).forEach((response) => {
                const user = response.userId;
                if (!user || typeof user !== 'object') {
                    return;
                }
                const userId = String(user._id || user.id || '');
                const label = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                if (
                    userId &&
                    label &&
                    user.userStatus !== UserType.TEACHER_TRAINER &&
                    !teachersById.has(userId)
                ) {
                    teachersById.set(userId, { id: userId, label });
                }
            });
        });

        return Array.from(teachersById.values()).sort((a, b) => a.label.localeCompare(b.label));
    };

    const getVisibleCategories = () => {
        if (currentUser?.userStatus === UserType.TEACHER) {
            return CATEGORY_ORDER.filter(isStudentType);
        }
        return CATEGORY_ORDER;
    };

    const getAvailableCategories = (assessmentsWithResponses) => {
        const typesWithData = new Set(
            (assessmentsWithResponses || [])
                .filter((assessment) => (assessment.responses || []).length > 0)
                .map((assessment) => assessment.type)
        );
        return getVisibleCategories().filter((type) => typesWithData.has(type));
    };

    const filterAssessmentsByDay = async (isCancelled) => {

        if (!assessments?.length) return;

       let filteredAssessments = assessments;

        // If the current user is a Teacher, filter assessments by type and user
        if (currentUser?.userStatus === UserType.TEACHER) {
            filteredAssessments = filteredAssessments.filter(assessment =>
                TEACHER_ASSESSMENT_TYPES.includes(assessment.type)
            );
        }

        // Further filter assessments based on the selected day
        filteredAssessments = filteredAssessments.filter(assessment =>
            assessment.day === selectedDay
        );

        // Create an array with essential details from filtered assessments
        const assessmentsInfo = filteredAssessments.map(({ _id, type, name, workshops, position }) => ({ _id, type, name, workshops, position }));

        // Fetch and attach responses for each assessment
        let assessmentsWithResponses = await Promise.all(
            assessmentsInfo.map(async ({ _id, type, name, workshops, position }) => {
                const responses = await fetchResponsesByAssessment(_id);

                return {
                    _id,
                    name,
                    type,
                    position,
                    workshops: workshops || [],
                    responses: (responses || []).map(r => ({
                        ...r,
                        survey: (workshops && workshops.length > 0)
                            ? groupQuestionsByWorkshop(workshops, r.survey).flatMap(w => w.questions || [])
                            : (r.survey || [])
                    }))
                };
            })
        );

        if (typeof isCancelled === 'function' && isCancelled()) {
            return;
        }

        const participants = buildParticipants(assessmentsWithResponses);
        const nextSelectedUser = selectedUser && participants.some((participant) => participant.id === selectedUser)
            ? selectedUser
            : '';
        const teacherOptions = buildTeachers(assessmentsWithResponses);
        const nextSelectedTeacher = selectedTeacher && teacherOptions.some((teacher) => teacher.id === selectedTeacher)
            ? selectedTeacher
            : '';

        setAllUsers(participants);
        setTeachers(teacherOptions);
        if (nextSelectedUser !== selectedUser) {
            setSelectedUser(nextSelectedUser);
        }
        if (nextSelectedTeacher !== selectedTeacher) {
            setSelectedTeacher(nextSelectedTeacher);
        }

        const categories = getAvailableCategories(assessmentsWithResponses);
        if (categories.length > 0 && !categories.includes(selectedCategory)) {
            setSelectedCategory(categories[0]);
        } else if (categories.length === 0) {
            setSelectedCategory('');
        }

        setSelectedDayAssessments(assessmentsWithResponses);
        setTotalAssessments(assessmentsWithResponses.length);
        updateChartsFromAssessments(assessmentsWithResponses, nextSelectedUser, nextSelectedTeacher);
    }

    // Reset AI summaries when monitoring or day changes
    useEffect(() => {
        setAiSummaries({});
        setLoadingSummaries({});
    }, [selectedMonitoring, selectedDay]);

    const handleChangeParticipant = (participantId) => {
        setSelectedUser(participantId || '');
        updateChartsFromAssessments(selectedDayAssessments, participantId || '', selectedTeacher);
    };

    const handleChangeTeacher = (teacherId) => {
        setSelectedTeacher(teacherId || '');
        updateChartsFromAssessments(selectedDayAssessments, selectedUser, teacherId || '');
    };

    /**
     * Handles the change of the selected day for assessments.
     * @param {Event} event - The event object from the day selection input.
    */
    const handleChangeDay = (event) => {
        setSelectedDay(event.target.value);
    };

    /**
     * Handle the selection change of a monitoring
    */
    const handleChangeMonitoring = (event) => {
        setSelectedMonitoring(monitorings.find(monitoring => monitoring._id === event.target.value));
        setSelectedMonitoringId(event.target.value);

        // Reset the form and assessments related state
        setAssessments([]);
        setSelectedCategory('');
        setDays([]);
        setSelectedDay('');
        setSelectedDayAssessments([]);
        setTotalAssessments(0);
        setLatestResponseDate('');
        setChartData([]);
        setCommentData([]);
        setSelectedUser('');
        setAllUsers([]);
        setSelectedTeacher('');
        setTeachers([]);
        setReportsView('results');
        setCourseImprovement(null);
        setCourseImprovementJob(null);
        setCourseImprovementsError(false);
    };

    /**
     * Retrieves all responses for a specific assessment by its ID.
     * @param {string} assessmentId - The unique identifier of the assessment.
     * @returns {Object[]} - The array of response objects returned from the server.
     */
    const fetchResponsesByAssessment = async (assessmentId) => {
        try {
            const token = localStorage.getItem("token");

            const response = await axios.get(`${BACKEND_URL}/responses/assessment/${assessmentId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching responses: ${error}`);
        }
    }

    

    /**
     * Determines the most recent response date from an array of responses.
     * @param {Array} responses - The array of response objects, each containing a 'date' field.
     * @return {Date|null} - The latest date found in the responses, or null if no responses are provided.
    */
    const getLatestResponseDate = (responses) => {

        if (responses.length === 0) return null;

        let latest = new Date(responses[responses.length-1].completionDate); // Assuming each response object has a "date" field.
        responses.forEach(response => {
            const responseDate = new Date(response.date);
            if (responseDate > latest) latest = responseDate;
        });

        return latest;
    }

    /**
     * Converts an array of assessment data into a CSV format string.
     * Includes fixed headers for general assessment information, question details, and response data.
     * The function handles various question types and includes a column for proposed choices.
     * It also includes detailed logging for debugging purposes.
     * 
     * @param {Array} data - The data to be converted to CSV format. Each item in the array should be an object
     *                       representing a single response to a question, with properties matching the headers.
     * @returns {string} A string formatted as CSV content, with headers and data rows separated by newlines.
     *                   Returns an empty string if the input data is invalid.
     */
    const convertToCSV = (data) => {
        const escapeCSVValue = (value) => {
            if (value == null) return '';
            value = value.toString();
            if (value.includes(',') || value.includes('\n') || value.includes('\r') || value.includes('"')) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        };

        if (!data || !Array.isArray(data) || data.length === 0) {
            console.error('convertToCSV: Invalid data array');
            return '';
        }

        const headers = [
            "Assessment Name", "Assessment Type", "Linking ID", "Record ID",
            "Display Name", "Completion Date", "Question Short Name", "Section", 
            "Question Type", "Question Item", "Matrix ID", "Matrix Title", "Proposed Choices", "Response", "Correct Answer"
        ];

        console.log('Headers:', headers);
        console.log('First data item:', data[0]);

        const csvContent = [
            headers.join(','),
            ...data.map(item => {
                const row = headers.map(header => {
                    // Convert header to camelCase for object key matching
                    const key = header.toLowerCase().split(' ').map((word, index) => 
                        index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)
                    ).join('');
                    const value = item[key];
                    console.log(`Field: ${key}, Value: ${value}`);
                    return escapeCSVValue(value);
                });
                console.log('Row:', row);
                return row.join(',');
            })
        ];

        console.log('CSV Content (first 3 lines):', csvContent.slice(0, 3));

        return csvContent.join('\n');
    };

    /**
     * Exports assessment data as a single CSV file in long format.
     * This function handles the entire export process, including data conversion, CSV creation, and file download.
     * 
     * The process includes the following steps:
     * 1. Converts selected day assessments to a long format.
     * 2. Converts the long format data to CSV.
     * 3. Creates a Blob with the CSV data.
     * 4. Initiates a download of the CSV file.
     * @throws {Error} If there's an issue during the export process, it will be caught and logged.
     */
    const handleExportData = () => {
        console.log('Starting export process...');
        console.log('Selected day assessments:', selectedDayAssessments);

        try {
            // Convert the data to long format
            const longFormatData = convertToLongFormat(
                applyResultFilters(selectedDayAssessments, selectedUser, selectedTeacher)
            );

            if (longFormatData.length === 0) {
                console.error('No data to export after conversion to long format');
                // You might want to show an error message to the user here
                return;
            }

            // Convert the long format data to CSV
            const csvData = convertToCSV(longFormatData);

            // Create a Blob with the CSV data
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

            // Create a link element, use it to download the csv file
            const link = document.createElement("a");
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", "raw_data.csv");
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                console.log('CSV file download initiated');
            } else {
                console.error('Browser does not support downloading');
            }
        } catch (error) {
            console.error('Error in export process:', error);
        }
    };


   /**
     * Converts a nested structure of assessment data into a flat, long format array.
     * This function processes assessment data, including responses and survey items,
     * and transforms it into a format suitable for CSV export.
     *
     * @param {Array} data - An array of assessment objects. Each assessment object should contain:
     *   - name: string (assessment name)
     *   - type: string (assessment type)
     *   - responses: Array of response objects, each containing:
     *     - linkingId: string
     *     - displayName: string
     *     - completionDate: string (ISO date string)
     *     - survey: Array of survey item objects, each containing:
     *       - shortName: string
     *       - workshopId: string (optional)
     *       - questionType: string
     *       - question: string
     *       - matrixId: string (optional, for matrix questions)
     *       - matrixTitle: string (optional, for matrix questions)
     *       - items: Array (for matrix questions)
     *       - choices: Array or Object of proposed choices
     *       - response: string or Array
     *       - correctAnswer: string or Array (optional)
     *
     * @returns {Array} A flat array where each item represents a single response to a question,
     *                  including all relevant metadata. Returns an empty array if input is invalid.
     */
    const convertToLongFormat = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        console.error('Invalid or empty data array passed to convertToLongFormat');
        return [];
    }

    return data.flatMap(assessment => {
        if (!assessment || !Array.isArray(assessment.responses)) {
            console.warn('Invalid assessment object:', assessment);
            return [];
        }

        return assessment.responses.flatMap(response => {
            if (!response || !Array.isArray(response.survey)) {
                console.warn('Invalid response object:', response);
                return [];
            }

            return response.survey
                // Filter out single-text questions
                .filter(surveyItem => surveyItem.questionType !== 'single-text')
                .flatMap(surveyItem => {
                    if (!surveyItem) {
                        console.warn('Invalid survey item:', surveyItem);
                        return [];
                    }

                    const baseData = {
                        assessmentName: assessment.name || '',
                        assessmentType: assessment.type || '',
                        linkingId: surveyItem.linkingId || '',
                        recordId: response.recordId || '',
                        displayName: response.displayName || '',
                        completionDate: response.completionDate ? new Date(response.completionDate).toISOString().split('T')[0] : '',
                        questionShortName: surveyItem.shortName || '',
                        workshopId: surveyItem.workshopId || '',
                        questionType: surveyItem.questionType || '',
                        matrixId: surveyItem.matrixId || '',
                        matrixTitle: surveyItem.matrixTitle || '',
                    };

                    const getProposedChoices = (choices) => {
                        if (Array.isArray(choices)) {
                            return choices.join(';');
                        } else if (typeof choices === 'object') {
                            return Object.values(choices).join(';');
                        }
                        return '';
                    };

                    if (surveyItem.questionType === 'checkbox' || surveyItem.questionType === 'radio-unordered') {
                        return [{
                            ...baseData,
                            questionItem: surveyItem.question || '',
                            proposedChoices: getProposedChoices(surveyItem.choices),
                            response: Array.isArray(surveyItem.response) ? surveyItem.response.join(';') : (surveyItem.response || ''),
                            correctAnswer: Array.isArray(surveyItem.correctAnswer) ? surveyItem.correctAnswer.join(';') : (surveyItem.correctAnswer || '')
                        }];
                    } else {
                        return [{
                            ...baseData,
                            questionItem: surveyItem.question || '',
                            proposedChoices: getProposedChoices(surveyItem.choices),
                            response: Array.isArray(surveyItem.response) ? surveyItem.response.join(";") : (surveyItem.response || ''),
                            correctAnswer: surveyItem.correctAnswer || ''
                        }];
                    }
                });
            });
        });
    };

    /**
     * Initiates the export of a PDF report based on a selected monitoring and day. It sends a request to the server to generate the PDF, then downloads it.
     * This function assumes the presence of a selected monitoring and day; it checks and aborts if either is missing.
     * @throws {Error} If the PDF export fails, it logs the error.
     */
    const handleExportPdfReport = async () => {
        try {
            setIsExportingPDF(true);

            // Check if a monitoring is selected
            if (!selectedMonitoring || !selectedDay) {
                console.error('No monitoring selected');
                return;
            }
            const token = localStorage.getItem("token");

            // Send a POST request to your backend endpoint
            const response = await axios.post(`${BACKEND_URL}/export/pdf`, 
                { 
                    assessments: applyResultFilters(selectedDayAssessments, selectedUser, selectedTeacher),
                    monitoring: selectedMonitoring, 
                    selectedDay: selectedDay,
                    status: currentUser.userStatus,
                    userId: currentUser._id,
                    language: languageCode,
                    sandbox: currentUser.sandbox
                }, 
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            // Create a Blob from the PDF Stream
            const file = new Blob([response.data], { type: 'application/pdf' });

            // Use file-saver to save the file on the client's machine
            saveAs(file, 'report.pdf');
        } catch (error) {
            console.error('Error exporting PDF:', error);
        } finally {
            setIsExportingPDF(false);
        }
    };

    /**
     * Initiates the export of a DOCX report based on a selected monitoring and day. It sends a request to the server to generate the DOCX, then downloads it.
     * This function assumes the presence of a selected monitoring and day; it checks and aborts if either is missing.
     * @throws {Error} If the DOCX export fails, it logs the error.
     */
    const handleExportDocxReport = async () => {
        try {
            setIsExportingDOCX(true);

            // Check if a monitoring is selected
            if (!selectedMonitoring || !selectedDay) {
                console.error('No monitoring selected');
                return;
            }
            const token = localStorage.getItem("token");

            // Send a POST request to your backend endpoint
            const response = await axios.post(`${BACKEND_URL}/export/docx`, 
                { 
                    assessments: applyResultFilters(selectedDayAssessments, selectedUser, selectedTeacher),
                    monitoring: selectedMonitoring, 
                    selectedDay: selectedDay,
                    status: currentUser.userStatus,
                    userId: currentUser._id,
                    language: languageCode,
                    sandbox: currentUser.sandbox
                }, 
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            // Create a Blob from the PDF Stream
            const file = new Blob([response.data], { type: 'application/docx' });

            // Use file-saver to save the file on the client's machine
            saveAs(file, 'report.docx');
        } catch (error) {
            console.error('Error exporting DOCX:', error);
        } finally {
            setIsExportingDOCX(false);
        }
    };

    /**
     * Organizes chart data by type, using a mapping to specific processing functions.
     * Defaults to an empty array for unhandled types.
     * @param {Array} data - The dataset to process.
     * @returns {Object} - Chart data organized by type.
     */
    const handlePrepareChartData = (data) => {
        let chartDataByType = {};
    
        // Get all unique types from the data
        const uniqueTypes = Array.from(new Set(data.map(item => item.type)));
    
        // Prepare chart data for each unique type using the prepareChartData function
        uniqueTypes.forEach(type => {
            chartDataByType[type] = prepareChartData(data, type);
        });
    
        return chartDataByType;
    };

    /**
     * Organizes comment data by type, applying specific processing functions dynamically.
     * Defaults to an empty array for types without a specific function.
     * @param {Array} data - The dataset to process.
     * @returns {Object} - Processed data organized by type.
     */
    const handlePrepareCommentData = (data) => {
        let commentDataByType = {};

        // Get all unique types from the data
        const uniqueTypes = Array.from(new Set(data.map(item => item.type)));

        // Prepare comment data for each unique type using the prepareCommentData function
        uniqueTypes.forEach(type => {
            commentDataByType[type] = prepareCommentData(data, type);
        });

        return commentDataByType;
    };

    /**
     * Groups chart data by assessment type and workshop, aggregating related items under their specific assessment and workshop categories.
     * @param {string} assessmentType - The type of assessment to filter and group data by.
     * @returns {Object} An object with nested structure where each key is an assessment name, containing objects that represent workshops, which in turn hold arrays of related chart items.
    */
    const groupChartData = (assessmentType) => {
        let groupedData = {};

        if (chartData[assessmentType]) {
            const allWorkshops = (selectedDayAssessments || []).flatMap(a => a.workshops || []);

            Object.keys(chartData[assessmentType]).forEach(workshopKey => {

                chartData[assessmentType][workshopKey].forEach(item => {

                    const assessmentKey = item.assessmentName || "default";
                    const label = workshopKey === 'default'
                        ? 'default'
                        : (getWorkshopDetailsById(allWorkshops, workshopKey)?.label || 'default');

                    if (!groupedData[assessmentKey]) {
                        groupedData[assessmentKey] = {};
                    }
                    if (!groupedData[assessmentKey][label]) {
                        groupedData[assessmentKey][label] = [];
                    }

                    groupedData[assessmentKey][label].push(item);
                });
            });
        }
        
        // Reorder assessments strictly by position (ascending) like /dashboard
        if (Object.keys(groupedData).length > 0) {
            // Names of assessments to display, ordered by their position
            const assessmentNamesInPositionOrder = (selectedDayAssessments || [])
                .filter(assessment => assessment.type === assessmentType)
                .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER))
                .map(assessment => assessment.name);

            // Rebuild the grouped result using the position order
            const groupedByAssessmentThenWorkshopInPositionOrder = {};
            assessmentNamesInPositionOrder.forEach(assessmentName => {
                if (groupedData[assessmentName]) {
                    groupedByAssessmentThenWorkshopInPositionOrder[assessmentName] = groupedData[assessmentName];
                }
            });

            // Add any remaining assessments that were not in the ordered list (if any)
            Object.keys(groupedData).forEach(assessmentName => {
                if (!groupedByAssessmentThenWorkshopInPositionOrder[assessmentName]) {
                    groupedByAssessmentThenWorkshopInPositionOrder[assessmentName] = groupedData[assessmentName];
                }
            });

            return groupedByAssessmentThenWorkshopInPositionOrder;
        }
        
        return groupedData;
    };

    /**
     * Groups comment data by assessment type and workshop, organizing items under their respective
     * assessment names and workshops. This structure facilitates easy access to comments related
     * to specific workshops within each assessment.
     * 
     * @param {string} assessmentType - The type of assessment to categorize comments by.
     * @returns {Object} - A nested object with assessment names as keys, each containing workshop keys that map to arrays of related items.
     */
    const groupCommentData = (assessmentType) => {
        let groupedData = {};

        if (commentData[assessmentType]) {
            const allWorkshops = (selectedDayAssessments || []).flatMap(a => a.workshops || []);

            Object.entries(commentData[assessmentType]).forEach(([workshopKey, items]) => {
                items.forEach(item => {
                    const assessmentKey = item.assessmentName || 'default';
                    const label = workshopKey === 'default'
                        ? 'default'
                        : (getWorkshopDetailsById(allWorkshops, workshopKey)?.label || 'default');

                    if (!groupedData[assessmentKey]) {
                        groupedData[assessmentKey] = {};
                    }
                    if (!groupedData[assessmentKey][label]) {
                        groupedData[assessmentKey][label] = [];
                    }
                    groupedData[assessmentKey][label].push(item);
                });
            });
        }
        
        // Reorder assessments strictly by position (ascending) like /dashboard
        if (Object.keys(groupedData).length > 0) {
            const ordering = (selectedDayAssessments || [])
                .filter(a => a.type === assessmentType)
                .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER))
                .map(a => a.name);

            const ordered = {};
            ordering.forEach(name => {
                if (groupedData[name]) ordered[name] = groupedData[name];
            });
            Object.keys(groupedData).forEach(name => {
                if (!ordered[name]) ordered[name] = groupedData[name];
            });
            return ordered;
        }
        
        return groupedData;
    };

    const handleRefresh = async () => {
        if (!selectedMonitoring || !selectedDay) return;
        
        setIsRefreshing(true);
        try {
            await filterAssessmentsByDay();
        } catch (error) {
            console.error('Failed to refresh assessments:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    /**
     * Asks the backend to generate course improvement suggestions from all responses of the
     * selected monitoring. The backend answers immediately with the pending job; the report is
     * generated in the background and picked up by the polling effect above.
     */
    const handleRequestCourseImprovements = async () => {
        if (!selectedMonitoring?._id) return;

        setIsRequestingCourseImprovements(true);
        setCourseImprovementsError(false);
        try {
            const response = await axios.post(
                `${BACKEND_URL}/aiBeacon/monitorings/${selectedMonitoring._id}/course-improvements`,
                {},
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setCourseImprovementJob(response.data?.currentJob || null);
        } catch (error) {
            console.error('Error requesting course improvements:', error);
            setCourseImprovementsError(true);
        } finally {
            setIsRequestingCourseImprovements(false);
        }
    };

    /**
     * Generates AI summary for text question responses via backend endpoint
     * @param {Array} responses - Array of response strings
     * @param {string} questionText - The question text
     * @param {string} questionKey - Unique key for the question
     * @returns {Promise<string>} - AI generated summary
     */
    const generateAiSummary = async (responses, questionText, questionKey) => {
        // Filter out null/empty responses
        const validResponses = responses.filter(r => r !== null && r !== '');
        
        // Si aucune réponse valide, ne rien faire
        if (validResponses.length === 0) {
            return null;
        }

        try {
            // Indiquer que ce résumé est en cours de chargement
            setLoadingSummaries(prev => ({ ...prev, [questionKey]: true }));

            // Appeler l'API backend
            const response = await axios.post(
                `${BACKEND_URL}/ai-tools/generate-text-summary`,
                { 
                    responses: validResponses,
                    questionText: questionText,
                    language: languageCode,
                    sandbox: currentUser?.sandbox || false
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            // Récupérer le résumé de la réponse
            const summary = response.data?.summary || null;
            
            // Stocker le résumé dans l'état
            setAiSummaries(prev => ({ ...prev, [questionKey]: summary }));
            
            // Indiquer que le chargement est terminé
            setLoadingSummaries(prev => ({ ...prev, [questionKey]: false }));
            
            return summary;
        } catch (error) {
            console.error('Error generating AI summary:', error);
            // En cas d'erreur, arrêter le loading
            setLoadingSummaries(prev => ({ ...prev, [questionKey]: false }));
            return null;
        }
    };

    const selectedParticipantLabel = allUsers.find((participant) => participant.id === selectedUser)?.label || '';
    const showTeacherFilter = currentUser?.userStatus === UserType.TEACHER_TRAINER && isStudentType(selectedCategory);
    const canShowSuggestions = Boolean(
        currentUser?.aiBeaconApiKeyCreatedAt && selectedMonitoring?.courseAiBeaconId
    );
    const showResultsView = !canShowSuggestions || reportsView === 'results';
    const filteredDayAssessments = applyResultFilters(selectedDayAssessments, selectedUser, selectedTeacher);
    const availableCategories = getVisibleCategories().filter((type) =>
        (selectedDayAssessments || []).some((assessment) => assessment.type === type)
    );
    const assessmentsForCategory = (filteredDayAssessments || []).filter(
        (assessment) => assessment.type === selectedCategory && (assessment.responses || []).length > 0
    );
    const rankingAssessments = assessmentsForCategory;
    const exportButtonSx = {
        ...buttonStyle,
        mr: 0,
        minWidth: 'unset',
        width: 40,
        height: 40,
        padding: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        boxShadow: 'none',
        border: '1px solid #e6e6e6',
        '&:hover': { backgroundColor: '#f7f7f7' },
    };

    return (
    <Box display="flex" sx={{ height: '100%', overflow: 'hidden', maxWidth: '100vw', bgcolor: '#f9f9f9' }}>
        <Sidebar/>

        <Box flex={1} sx={{ minWidth: 0, minHeight: 0, height: '100%', overflow: 'auto', bgcolor: '#f9f9f9' }}>

            <Box sx={{ mt: { xs: 1, md: '10px' } }}>
                <Topbar title={getMessage("label_my_results")} />
            </Box>

            <Box sx={{ mx: { xs: 2, md: 3 }, mb: 3, pb: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', md: 0 }, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 100%', md: '0 1 260px' } }}>
                        <InputLabel id="monitoring">{getMessage("label_choose_monitoring")}</InputLabel>
                        <Select
                            labelId="monitoring"
                            id="monitoring"
                            value={selectedMonitoringId}
                            onChange={handleChangeMonitoring}
                            label={getMessage("label_choose_monitoring")}
                        >
                            {monitorings && monitorings.map((monitoring) => (
                                <MenuItem key={monitoring._id} value={monitoring._id}>
                                    {monitoring.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {canShowSuggestions && (
                        <ToggleButtonGroup
                            value={reportsView}
                            exclusive
                            size="small"
                            onChange={(_, val) => { if (val !== null) setReportsView(val); }}
                            sx={{ flexShrink: 0 }}
                        >
                            <ToggleButton value="results" sx={{ textTransform: 'none' }}>
                                {getMessage("label_reports_view_results")}
                            </ToggleButton>
                            <ToggleButton value="suggestions" sx={{ textTransform: 'none' }}>
                                {getMessage("label_reports_view_suggestions")}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {showResultsView && (
                    <>
                    <Box display="flex" alignItems="center" gap={1} sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 0, flex: { md: '0 1 260px' } }}>
                        <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 0 }}>
                            <InputLabel id="day">{getMessage("label_choose_session")}</InputLabel>
                            <Select
                                labelId="day"
                                id="day"
                                value={(days.length === 0 && selectedDay !== '') ? '' : selectedDay}
                                onChange={handleChangeDay}
                                label={getMessage("label_choose_session")}
                            >
                                {days.length > 0 ? (
                                    days.map((day, index) => (
                                        <MenuItem key={index} value={day}>
                                            {day}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        {getMessage("label_no_sessions_available")}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Tooltip title={getMessage("label_refresh_answers")}>
                            <span>
                                <IconButton
                                    onClick={handleRefresh}
                                    disabled={!selectedDay || isRefreshing}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {isRefreshing ? <CircularProgress size={22} /> : <RefreshIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 100%', md: '1 1 240px' }, maxWidth: { md: 320 } }}>
                        <InputLabel id="participant" shrink>{getMessage("label_choose_participant")}</InputLabel>
                        <Select
                            labelId="participant"
                            id="participant"
                            value={selectedUser}
                            onChange={(event) => handleChangeParticipant(event.target.value)}
                            label={getMessage("label_choose_participant")}
                            displayEmpty
                            notched
                            disabled={!selectedDay}
                        >
                            <MenuItem value="">{getMessage("label_all_participants")}</MenuItem>
                            {allUsers.map((participant) => (
                                <MenuItem key={participant.id} value={participant.id}>
                                    {participant.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ ml: { xs: 0, md: 'auto' }, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 1, width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={hideRankingNames}
                                    onChange={(event) => setHideRankingNames(event.target.checked)}
                                />
                            }
                            label={getMessage("label_hide_ranking_names")}
                            sx={{
                                mr: 0,
                                flexShrink: 0,
                                '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', whiteSpace: 'nowrap' },
                            }}
                        />
                        <Divider orientation="vertical" flexItem />
                        <ToggleButtonGroup
                            value={showPercentage ? 'pct' : 'count'}
                            exclusive
                            size="small"
                            onChange={(_, val) => { if (val !== null) setShowPercentage(val === 'pct'); }}
                            sx={{ flexShrink: 0 }}
                        >
                            <ToggleButton value="count">#</ToggleButton>
                            <ToggleButton value="pct">%</ToggleButton>
                        </ToggleButtonGroup>
                        <Divider orientation="vertical" flexItem />
                        <Box display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                            <Tooltip title="DOCX">
                                <span>
                                    <Button onClick={handleExportDocxReport} variant="contained" sx={exportButtonSx} disabled={!selectedDayAssessments || selectedDayAssessments.length === 0}>
                                        {isExportingDOCX ? <CircularProgress size={20} /> : <img src={DOClogo} alt="DOCX" style={{ width: 22, height: 22 }} />}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="PDF">
                                <span>
                                    <Button onClick={handleExportPdfReport} variant="contained" sx={exportButtonSx} disabled={!selectedDayAssessments || selectedDayAssessments.length === 0}>
                                        {isExportingPDF ? <CircularProgress size={20} /> : <img src={PDFlogo} alt="PDF" style={{ width: 22, height: 22 }} />}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="CSV">
                                <span>
                                    <Button onClick={handleExportData} variant="contained" sx={exportButtonSx} disabled={!selectedDayAssessments || selectedDayAssessments.length === 0}>
                                        <img src={CSVlogo} alt="CSV" style={{ width: 22, height: 22 }} />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    </>
                    )}
                </Box>

                {canShowSuggestions && reportsView === 'suggestions' ? (
                    <CourseImprovementsPanel
                        courseImprovement={courseImprovement}
                        isLoading={isLoadingCourseImprovement}
                        courseImprovementJob={courseImprovementJob}
                        isGenerating={isRequestingCourseImprovements || isCourseImprovementPending}
                        canGenerate={Boolean(assessments && assessments.length > 0)}
                        generateError={courseImprovementsError}
                        getMessage={getMessage}
                        onGenerate={handleRequestCourseImprovements}
                    />
                ) : (
                    <>
                {selectedDay && (
                    <Typography variant="body2" color="text.secondary">
                        {selectedUser
                            ? `${getMessage("label_results_for_participant")} ${hideRankingNames ? getMessage("label_anonymous") : selectedParticipantLabel}`
                            : getMessage("label_results_for_all")}
                    </Typography>
                )}

                {selectedDay && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        {availableCategories.map((type) => {
                            const selected = selectedCategory === type;
                            const config = getAssessmentTypeConfig(type);
                            return (
                                <Box
                                    key={type}
                                    component="button"
                                    type="button"
                                    onClick={() => setSelectedCategory(type)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid',
                                        borderColor: config.color,
                                        backgroundColor: selected ? `${config.color}28` : `${config.color}10`,
                                        boxShadow: selected ? `inset 0 0 0 1px ${config.color}` : 'none',
                                        color: config.color,
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: `${config.color}20`,
                                        },
                                    }}
                                >
                                    <Typography component="span" sx={{ fontSize: '16px', lineHeight: 1 }}>
                                        {config.icon}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: selected ? 700 : 500,
                                            lineHeight: 1.2,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {getMessage(config.name) || localizeAssessmentType(type, getMessage)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {selectedDay && selectedCategory ? (
                    <AssessmentResultStack
                        assessments={assessmentsForCategory}
                        rankingAssessments={rankingAssessments}
                        groupChartData={groupChartData}
                        groupCommentData={groupCommentData}
                        hide_students_name={currentUser?.userStatus === UserType.TEACHER_TRAINER}
                        showTeacherFilter={showTeacherFilter}
                        teachers={teachers}
                        selectedTeacher={selectedTeacher}
                        handleChangeTeacher={handleChangeTeacher}
                        aiSummaries={aiSummaries}
                        loadingSummaries={loadingSummaries}
                        showPercentage={showPercentage}
                        hideValueLabels={false}
                        showChoiceLabels={Boolean(selectedUser)}
                        highlightedParticipant={hideRankingNames ? '' : selectedParticipantLabel}
                        anonymizeRanking={hideRankingNames}
                        onGenerateSummary={generateAiSummary}
                    />
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                        {getMessage("label_choose_monitoring")}
                    </Typography>
                )}
                    </>
                )}
            </Box>
        </Box>
    </Box>);
};

export default Reports;